import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useTranslation } from "react-i18next";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import { DatePickerField, InputField } from "@/features/FormCard/Fields";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";

import { FormActionsNew } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

const FormSchema = z.object({
  strategy_from_id: z.coerce.number().optional().nullable(),
  strategy_to_id: z.coerce.number().optional().nullable(),
  quantity: z.coerce.number().optional().nullable(),
  date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  weight: z.coerce.number().optional().nullable(),
  market_value_local_ccy: z.coerce.number().optional().nullable(),
  market_value_ptf_ccy: z.coerce.number().optional().nullable(),
  time_serie_file_id: z.coerce.number().optional().nullable(),
});
const formSchemaFields = FormSchema.keyof().enum;

export function E2PositionsNew() {
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client: Clients.E2,
    table_name: Tables.E2_POSITIONS,
    FormSchema,
    isNew: true,
  });

  const { t } = useTranslation();

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${Tables.E2_POSITIONS}.new_position`, {
                  defaultValue: "New position",
                })}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.strategy_to_id}
                    label={t(`${Tables.E2_POSITIONS}.columns.strategy_to_id`, {
                      ns: "tables",
                      defaultValue: "Strategy target id",
                    })}
                    control={form.control}
                    isRequired
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.strategy_from_id}
                    label={t(
                      `${Tables.E2_POSITIONS}.columns.strategy_from_id`,
                      {
                        ns: "tables",
                        defaultValue: "Strategy source id",
                      },
                    )}
                    control={form.control}
                    isRequired
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formSchemaFields.date}
                    label={t(`${Tables.E2_POSITIONS}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.quantity}
                    label={t(`${Tables.E2_POSITIONS}.columns.quantity`, {
                      ns: "tables",
                      defaultValue: "Quantity",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.weight}
                    label={t(`${Tables.E2_POSITIONS}.columns.weight`, {
                      ns: "tables",
                      defaultValue: "Weight",
                    })}
                    control={form.control}
                  />
                </div>

                <Divider />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.market_value_local_ccy}
                    label={t(
                      `${Tables.E2_POSITIONS}.columns.market_value_local_ccy`,
                      {
                        ns: "tables",
                        defaultValue: "Market value (local currency)",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.market_value_ptf_ccy}
                    label={t(
                      `${Tables.E2_POSITIONS}.columns.market_value_ptf_ccy`,
                      {
                        ns: "tables",
                        defaultValue: "Market value (portfolio currency)",
                      },
                    )}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
        >
          {changedFields
            ? NewDiff(changedFields, [], {}, Tables.E2_POSITIONS)
            : t("no_changes")}
        </ConfirmationModal>

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
